import { supabase } from './supabaseClient';
import type { TaskAssignment, TaskRow } from '../types';

export const taskAssignmentService = {
  /**
   * Carrega todas as atribuições de um projeto (via tasks.project_id)
   */
  async getByProject(projectId: string): Promise<TaskAssignment[]> {
    const { data, error } = await supabase
      .from('task_assignments')
      .select('*, tasks!inner(project_id)')
      .eq('tasks.project_id', projectId);

    if (error) throw error;
    return (data || []).map((row: Record<string, unknown>) => ({
      id: row.id as string,
      task_id: row.task_id as string,
      team_member_id: row.team_member_id as string,
      is_primary: row.is_primary as boolean,
      created_at: row.created_at as string,
    }));
  },

  /**
   * Carrega atribuições de uma tarefa específica
   */
  async getByTask(taskId: string): Promise<TaskAssignment[]> {
    const { data, error } = await supabase
      .from('task_assignments')
      .select('*')
      .eq('task_id', taskId)
      .order('is_primary', { ascending: false });

    if (error) throw error;
    return (data || []) as TaskAssignment[];
  },

  /**
   * Adiciona um membro como responsável de uma tarefa
   */
  async addAssignee(taskId: string, memberId: string, isPrimary = false): Promise<TaskAssignment | null> {
    // Se isPrimary, desmarcar o primary atual
    if (isPrimary) {
      await supabase
        .from('task_assignments')
        .update({ is_primary: false })
        .eq('task_id', taskId)
        .eq('is_primary', true);
    }

    const { data, error } = await supabase
      .from('task_assignments')
      .upsert({
        task_id: taskId,
        team_member_id: memberId,
        is_primary: isPrimary,
      }, { onConflict: 'task_id,team_member_id' })
      .select()
      .single();

    if (error) {
      if (error.message?.includes('AbortError')) return null;
      throw error;
    }
    return data as TaskAssignment;
  },

  /**
   * Remove um membro da tarefa
   */
  async removeAssignee(taskId: string, memberId: string): Promise<boolean> {
    const { error } = await supabase
      .from('task_assignments')
      .delete()
      .eq('task_id', taskId)
      .eq('team_member_id', memberId);

    if (error) throw error;
    return true;
  },

  /**
   * Define quem é o responsável principal de uma tarefa.
   *
   * CANÔNICO (Fase 5e — governança de sincronização):
   * Esta é a **única** forma permitida de mudar o responsável principal. Atualiza
   * atomicamente `task_assignments.is_primary` e `tasks.assigned`. Chamadores que
   * escrevem direto em `tasks.assigned` sem passar por aqui criam drift entre as duas
   * fontes (BUSINESS_RULES.md §sync).
   *
   * Se o membro ainda não estiver na junction, ele é inserido (upsert).
   * Passe `memberId = null` para remover o responsável principal.
   */
  async setPrimary(taskId: string, memberId: string | null): Promise<boolean> {
    // 1. Desmarcar todos os primary atuais
    await supabase
      .from('task_assignments')
      .update({ is_primary: false })
      .eq('task_id', taskId);

    // 2. Se memberId fornecido, garantir que existe e marcar como primary
    if (memberId) {
      const { error: upsertErr } = await supabase
        .from('task_assignments')
        .upsert({
          task_id: taskId,
          team_member_id: memberId,
          is_primary: true,
        }, { onConflict: 'task_id,team_member_id' });
      if (upsertErr) throw upsertErr;
    }

    // 3. Sincronizar espelho em tasks.assigned (mesmo valor, atomicamente)
    const { error: mirrorErr } = await supabase
      .from('tasks')
      .update({ assigned: memberId })
      .eq('id', taskId);
    if (mirrorErr) throw mirrorErr;

    return true;
  },

  /**
   * Remove todas as atribuições de um membro (usado quando deleta membro da equipe)
   */
  async removeAllByMember(memberId: string): Promise<boolean> {
    const { error } = await supabase
      .from('task_assignments')
      .delete()
      .eq('team_member_id', memberId);

    if (error) throw error;
    return true;
  },

  /**
   * Reatribui todas as tarefas de um membro para outro
   */
  async reassignAll(fromMemberId: string, toMemberId: string): Promise<boolean> {
    // Buscar atribuições do membro antigo
    const { data: assignments, error: fetchError } = await supabase
      .from('task_assignments')
      .select('*')
      .eq('team_member_id', fromMemberId);

    if (fetchError) throw fetchError;
    if (!assignments || assignments.length === 0) return true;

    // Para cada assignment, upsert com o novo membro
    for (const assignment of assignments) {
      await supabase
        .from('task_assignments')
        .upsert({
          task_id: assignment.task_id,
          team_member_id: toMemberId,
          is_primary: assignment.is_primary,
        }, { onConflict: 'task_id,team_member_id' });
    }

    // Remover atribuições do membro antigo
    await this.removeAllByMember(fromMemberId);
    return true;
  },

  /**
   * Busca tarefas atribuídas a uma lista de membros (substitui .in('assigned', memberIds))
   * Usado pelo MyTasksModule
   */
  async getTasksForMembers(memberIds: string[]): Promise<(TaskRow & { project_name: string })[]> {
    if (memberIds.length === 0) return [];

    const { data, error } = await supabase
      .from('task_assignments')
      .select('task_id, tasks(*, projects(name))')
      .in('team_member_id', memberIds);

    if (error) throw error;
    if (!data) return [];

    // Deduplica tasks (um membro pode ter múltiplas assignments na mesma task)
    const seen = new Set<string>();
    const result: (TaskRow & { project_name: string })[] = [];

    for (const row of data) {
      const task = (row as Record<string, unknown>).tasks as (TaskRow & { projects?: { name: string } | null }) | null;
      if (!task || task.type === 'group') continue;
      if (seen.has(task.id)) continue;
      seen.add(task.id);
      result.push({
        ...task,
        project_name: task.projects?.name || '',
      });
    }

    // Ordenar por end_date
    result.sort((a, b) => {
      if (!a.end_date) return 1;
      if (!b.end_date) return -1;
      return a.end_date.localeCompare(b.end_date);
    });

    return result;
  },

  /**
   * Insere atribuições em bulk (para import/AI)
   */
  async bulkAssign(assignments: { task_id: string; team_member_id: string; is_primary: boolean }[]): Promise<TaskAssignment[]> {
    if (assignments.length === 0) return [];

    const { data, error } = await supabase
      .from('task_assignments')
      .upsert(assignments, { onConflict: 'task_id,team_member_id' })
      .select();

    if (error) throw error;
    return (data || []) as TaskAssignment[];
  },

  /**
   * Sincroniza o campo tasks.assigned com o primary da junction table
   */
  async syncPrimaryToTask(taskId: string): Promise<string | null> {
    const { data, error } = await supabase
      .from('task_assignments')
      .select('team_member_id')
      .eq('task_id', taskId)
      .eq('is_primary', true)
      .maybeSingle();

    if (error) throw error;
    return data?.team_member_id || null;
  }
};
